import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Col, Modal, ModalBody, Row } from "reactstrap";
import { Pagination } from "antd";
import Loader from "../../../../components/Loader/Loader";
import PaginatorDropdown from "../../../../components/Datatable/PaginationDropdown";
import { pendingRefundEvents } from "../../../../actions/admin/scheduled-event-actions";
import { sendPostRequest } from "../../../../actions/app-actions";
import { getVenueIcon, notification } from "../../../../utils/utility";
import { formatBookingDate } from "./formatBookingDate";

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="2" y="3.5" width="12" height="10.5" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M2 6.5h12M5.5 2v3M10.5 2v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 5v3l1.8 1.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PersonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="5.5" r="2.8" stroke="currentColor" strokeWidth="1.4" />
    <path d="M2.5 14c0-3.04 2.46-5.5 5.5-5.5s5.5 2.46 5.5 5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const MailIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M2 4.5 8 9l6-4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const formatAmount = (value) => {
  const amount = Number(value);
  const safe = Number.isNaN(amount) ? 0 : amount;
  return `$${safe.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

const providerLabel = (provider) =>
  provider === "paypal" ? "Paid via PayPal" : "Paid via Stripe";

const PendingRefund = ({ pendingRefundEvents, sendPostRequest }) => {
  const [events, setEvents] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmEvent, setConfirmEvent] = useState(null);
  const [refundingId, setRefundingId] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const fetchEvents = useCallback(
    (nextPagination) => {
      setIsLoading(true);
      pendingRefundEvents({
        data: {
          pageSize: nextPagination.pageSize,
          pageNumber: nextPagination.current,
          sortField: "booking_date_time",
          sortOrder: "desc",
          filter: {},
        },
        onSuccess: (response) => {
          const list = response && Array.isArray(response.data) ? response.data : [];
          setEvents(list);
          setTotalEvents(response && response.total ? response.total : 0);
          setPagination(nextPagination);
          setIsLoading(false);
        },
        onError: () => {
          setEvents([]);
          setIsLoading(false);
          notification.error("Could not load events pending refund.");
        },
      });
    },
    [pendingRefundEvents]
  );

  useEffect(() => {
    fetchEvents({ current: 1, pageSize: 10 });
  }, [fetchEvents]);

  const toggleSelected = (event) => {
    setSelectedIds((current) =>
      current.includes(event.id)
        ? current.filter((id) => id !== event.id)
        : [...current, event.id]
    );
  };

  const executeRefund = () => {
    if (!confirmEvent || refundingId) {
      return;
    }
    const bookingId = confirmEvent.id;
    setRefundingId(bookingId);
    sendPostRequest({
      url: "/booking/refund",
      data: { booking_id: bookingId },
      onSuccess: (response) => {
        setRefundingId(null);
        const succeeded = response && (response.success === true || response.success === "true");
        if (succeeded) {
          notification.success(response.message || "Refund completed.");
          setConfirmEvent(null);
          setSelectedIds((current) => current.filter((id) => id !== bookingId));
          fetchEvents(pagination);
        } else {
          notification.error(
            (response && response.message) || "Could not refund this booking."
          );
        }
      },
      onError: () => {
        setRefundingId(null);
        notification.error("Could not refund this booking. Please try again.");
      },
    });
  };

  return (
    <div className="rd_pasevencontth se-refund">
      <Loader isShowLoader={isLoading} />
      <header className="se-refund__head">
        <h3>Events pending refund</h3>
        <p>These are events that should be refunded.</p>
      </header>

      {events.length > 0 ? (
        <div className="se-refund__list">
          {events.map((event) => {
            const selected = selectedIds.includes(event.id);
            const checkboxId = `refund-booking-${event.id}`;
            return (
              <article
                key={event.id}
                className={`se-refund-row${selected ? " is-selected" : ""}`}
              >
                <div className="se-refund-row__event">
                  <div className="se-refund-row__title">
                    <div className="se-card__check event-form-group">
                      <div className="form-check-box">
                        <input
                          type="checkbox"
                          id={checkboxId}
                          checked={selected}
                          onChange={() => toggleSelected(event)}
                        />
                        <label className="mb-0" htmlFor={checkboxId}>
                          <span></span>
                        </label>
                      </div>
                    </div>
                    <h3>{event.event_name}</h3>
                    <span className="se-refund-badge">Refund pending</span>
                  </div>
                  {event.booking_date ? (
                    <div className="se-refund-row__meta">
                      <CalendarIcon />
                      <span>{formatBookingDate(event.booking_date, "DD-MM-YYYY")}</span>
                    </div>
                  ) : null}
                  {event.start_time && event.end_time ? (
                    <div className="se-refund-row__meta">
                      <ClockIcon />
                      <span>
                        {event.start_time} – {event.end_time}
                      </span>
                    </div>
                  ) : null}
                </div>

                <div className="se-refund-row__invitee">
                  {event.attendee_name ? (
                    <div className="se-refund-row__meta">
                      <PersonIcon />
                      <span>{event.attendee_name}</span>
                    </div>
                  ) : null}
                  {event.attendee_email ? (
                    <div className="se-refund-row__meta">
                      <MailIcon />
                      <span>{event.attendee_email}</span>
                    </div>
                  ) : null}
                  {event.venue ? (
                    <div className="se-refund-row__meta">
                      {getVenueIcon(event.venue)}
                      <span>{event.venue}</span>
                    </div>
                  ) : null}
                </div>

                <div className="se-refund-row__amount">
                  <span>Amount paid</span>
                  <strong>{formatAmount(event.amount_paid)}</strong>
                  <em>{providerLabel(event.payment_provider)}</em>
                </div>

                <button
                  type="button"
                  className="se-btn se-btn--primary se-refund-row__action"
                  onClick={() => setConfirmEvent(event)}
                  disabled={refundingId === event.id}
                >
                  Execute refund
                </button>
              </article>
            );
          })}
        </div>
      ) : !isLoading ? (
        <div className="se-empty">
          <strong>No events pending refund</strong>
          <span>Cancelled paid bookings that still need a refund will show up here.</span>
        </div>
      ) : null}

      {events.length > 0 ? (
        <Row className="pb-4 pr-md-3 mt-3">
          <Col className="text-center" lg="12" md="12">
            <Pagination
              className="list-view-pagination"
              current={pagination.current}
              total={totalEvents}
              pageSize={pagination.pageSize}
              onChange={(page, pageSize) =>
                fetchEvents({ current: page, pageSize })
              }
              showSizeChanger={false}
            />
          </Col>
          <Col sm="6" className="table_paginaton">
            <PaginatorDropdown
              styleClasses="mt-3"
              pageSize={pagination.pageSize}
              onPageSizeChange={(e) =>
                fetchEvents({ current: 1, pageSize: Number(e.target.value) })
              }
            />
          </Col>
        </Row>
      ) : null}

      <Modal
        isOpen={Boolean(confirmEvent)}
        toggle={() => {
          if (!refundingId) {
            setConfirmEvent(null);
          }
        }}
        centered
        className="se-details-modal"
        contentClassName="se-details-modal__content"
      >
        <ModalBody>
          <div className="se-details">
            <header className="se-details__header">
              <div className="se-details__header-copy">
                <p className="se-details__eyebrow">Pending refund</p>
                <h2>Execute refund</h2>
              </div>
              <button
                type="button"
                className="se-details__close"
                onClick={() => setConfirmEvent(null)}
                aria-label="Close"
                disabled={Boolean(refundingId)}
              >
                ×
              </button>
            </header>
            <p className="se-confirm__copy">
              This sends the refund for{" "}
              <strong>{confirmEvent ? confirmEvent.event_name : "this booking"}</strong>
              {confirmEvent && confirmEvent.attendee_name
                ? ` to ${confirmEvent.attendee_name}`
                : ""}
              . Amount paid:{" "}
              <strong>{confirmEvent ? formatAmount(confirmEvent.amount_paid) : ""}</strong>
              {confirmEvent ? ` (${providerLabel(confirmEvent.payment_provider)})` : ""}.
              The amount returned follows this event&apos;s refund policy.
            </p>
            <div className="se-details__actions">
              <button
                type="button"
                className="se-btn se-btn--ghost"
                onClick={() => setConfirmEvent(null)}
                disabled={Boolean(refundingId)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="se-btn se-btn--primary"
                onClick={executeRefund}
                disabled={Boolean(refundingId)}
              >
                {refundingId ? "Refunding…" : "Execute refund"}
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default connect(null, {
  pendingRefundEvents,
  sendPostRequest,
})(PendingRefund);
